<template>
  <v-card
    v-if="!hide"
    v-intersect="{
      handler: (entry) => entry && sendPromptShownAnalytics(),
      options: { threshold: 0.7 }
    }"
    class="app-card border-post-detail-15 py-2"
    :class="{
      'mb-1px': $vuetify.display.mobile,
      'mb-4': !$vuetify.display.mobile
    }"
  >
    <div class="d-flex flex-column align-center">
      <v-card-title class="font-size-big font-weight-bold py-2">{{ title }}</v-card-title>
      <v-card-text class="text-center pt-0 pb-2">{{ message }}</v-card-text>

      <v-card-actions v-if="!enabled" class="pt-4 px-4">
        <the-button class="ml-0 px-4" small secondary @click.native="skip">{{ $translate("common.notNow") }}</the-button>

        <the-button v-if="blocked" class="px-6 ml-4" small :href="getPermissionsUpdateLink()" target="_blank">
          {{ $translate("DisabledPushNotificationsPrompt.moreInfo") }}
        </the-button>

        <the-button v-else class="px-6 ml-4" small :href="getPermissionsUpdateLink()" target="_blank" @click.native="askForNotificationsPermission">
          {{ $translate("DisabledPushNotificationsPrompt.turnOnNotifications") }}
        </the-button>
      </v-card-actions>
    </div>
  </v-card>
</template>

<script>
import { mapActions } from "pinia"

import { useAppStateStore } from "@/store/appState.js"

import * as pushNotifications from "@/services/pushNotifications.js"

import * as texts from "@/services/texts.js"

import { registerAnalyticsEvent } from "@/services/analytics.js"

export default {
  data() {
    return {
      enabled: false,
      blocked: false,
      hide: false,
      id: texts.generateUUID()
    }
  },
  computed: {
    title() {
      return this.blocked
        ? this.$translate("DisabledPushNotificationsPrompt.blockedTitle")
        : this.enabled
        ? this.$translate("DisabledPushNotificationsPrompt.enabledTitle")
        : this.$translate("DisabledPushNotificationsPrompt.getInstantUpdates")
    },
    message() {
      return this.blocked
        ? this.$translate("DisabledPushNotificationsPrompt.toUnblockNotifications")
        : this.enabled
        ? this.$translate("DisabledPushNotificationsPrompt.enabledMessage")
        : this.$translate("DisabledPushNotificationsPrompt.neverMissOut")
    }
  },
  created() {
    this.setBlocked()
    window.addEventListener("visibilitychange", this.hideIfPermissionsGranted)
    this.$eventBus.$on("enabled-pn", this.setEnabled)
    this.$eventBus.$on("blocked-pn", this.setBlocked)
  },
  beforeUnmount() {
    window.removeEventListener("visibilitychange", this.hideIfPermissionsGranted)
    this.$eventBus.$off("enabled-pn", this.setEnabled)
    this.$eventBus.$off("blocked-pn", this.setBlocked)
  },
  methods: {
    ...mapActions(useAppStateStore, ["setDisabledPushNotificationPromptSkipDate"]),
    getPermissionsUpdateLink() {
      if (Notification.permission === "default") {
        return null
      }

      return pushNotifications.getPermissionsUpdateLink()
    },
    async askForNotificationsPermission() {
      registerAnalyticsEvent({
        name: "notifications-prompt-agreed",
        attributes: { key: this.id }
      })

      if (Notification.permission === "default") {
        try {
          await pushNotifications.requestPermission()
          registerAnalyticsEvent({
            name: "notifications-prompt-enabled",
            attributes: { key: this.id }
          })

          if (Notification.permission === "granted") {
            this.$eventBus.$emit("enabled-pn")
          } else if (Notification.permission === "denied") {
            this.$eventBus.$emit("blocked-pn")
          }
        } catch (err) {
          registerAnalyticsEvent({
            name: "notifications-prompt-not-enabled",
            attributes: { key: this.id }
          })
          this.setDisabledPushNotificationPromptSkipDate()
          this.$reportError(err)
        }
      }
    },
    hideIfPermissionsGranted() {
      if (!this.hide && Notification.permission === "granted") {
        this.hide = true
        this.$eventBus.$emit("notify", this.$translate("DisabledPushNotificationsPrompt.enabled"))
      }
    },
    skip() {
      registerAnalyticsEvent({
        name: "notifications-prompt-snoozed",
        attributes: { key: this.id }
      })

      this.setDisabledPushNotificationPromptSkipDate()
    },
    sendPromptShownAnalytics() {
      registerAnalyticsEvent({
        name: "notifications-prompt-shown",
        attributes: { key: this.id }
      })
    },
    setEnabled() {
      if (Notification.permission === "granted") {
        this.enabled = true
        this.blocked = false
      }
    },
    setBlocked() {
      if (Notification.permission === "denied") {
        this.enabled = false
        this.blocked = true
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.mb-1px {
  margin-bottom: 1px;
}
</style>
